import React from 'react';

import { StandardEventProperties } from '@betterment/js-runtime';
import { Icon } from '@betterment/samba-react';
import { Box, ButtonProps, Flex, Menu, MenuButton, ThemeColor, theme } from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import { MenuItems, MenuLink, MenuList, MenuPopover } from '@reach/menu-button';

export type Data = { action: string; href: string; turbolinks?: boolean };
export type Item = {
  title: string;
  trackingEventData: StandardEventProperties;
  color?: ThemeColor;
  disabled?: boolean;
} & (
  | {
      path?: string;
      data?: Data;
    }
  | { onClick: () => void }
);
export type Separator = { separator: boolean };

export type ActionMenuProps = {
  menuItems: Array<Item | Separator>;
  buttonText?: string;
  iconName?: 'Ellipses' | 'Transfer';
  size?: 'default' | 'small';
  color?: 'bmtWhite' | 'bmtGrey90';
  portal?: boolean;
  trackingEventData?: StandardEventProperties;
} & Omit<ButtonProps, 'size' | 'color'>;

const StyledMenuButton = styled(MenuButton)`
  white-space: nowrap;
  margin: 0;
  width: 100%;
`;

const StyledMenuItems = styled(MenuItems)`
  border-color: ${theme.colors.bmtGrey30};
  padding-top: ${theme.space[3]};
  padding-bottom: ${theme.space[3]};
`;

const StyledMenuLink = styled(MenuLink)<{ disabled?: boolean; color?: ThemeColor }>`
  font-size: ${theme.fontSizes[2]};
  padding: ${theme.space[3]} ${theme.space[6]};
  white-space: break-spaces;
  font-family: ${theme.fonts.sans};

  &[data-reach-menu-item] {
    color: ${props => (props.color ? theme.colors[props.color] : theme.colors.bmtGrey90)};

    &[data-selected] {
      background: ${theme.colors.bmtGrey10};
    }
  }

  &:hover {
    text-decoration: none;
  }

  &[disabled] {
    color: ${theme.colors.bmtGrey40};
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const StyledMenuPopover = styled(MenuPopover)`
  &[data-reach-menu-popover] {
    margin-top: ${theme.space[3]};
    min-width: 200px;
    z-index: 100;
  }
`;
export const ActionMenuItems: React.FC<React.PropsWithChildren<Pick<ActionMenuProps, 'menuItems'>>> = ({
  menuItems,
}) => (
  <>
    {menuItems.map((item, index) => {
      let key: string | number = index;
      if ('title' in item) key = item.title;
      return (
        <div key={key}>
          {'separator' in item && <Box as="hr" mx="0" my="3" />}
          {'path' in item && (
            <StyledMenuLink
              href={item.disabled ? undefined : item.path}
              data-action={item.data?.action}
              data-turbolinks={item.data?.turbolinks}
              data-href={item.data?.href}
              data-track-event="ElementClicked"
              data-track-module={item.trackingEventData.module}
              data-track-name={item.trackingEventData.name}
              disabled={item.disabled}
              tabIndex={item.disabled ? -1 : 0}
              color={item.color}
            >
              {item.title}
            </StyledMenuLink>
          )}
          {'onClick' in item && (
            <StyledMenuLink
              data-track-event="ElementClicked"
              data-track-module={item.trackingEventData.module}
              data-track-name={item.trackingEventData.name}
              disabled={item.disabled}
              color={item.color}
              tabIndex={item.disabled ? -1 : 0}
              onClick={item.onClick}
            >
              {item.title}
            </StyledMenuLink>
          )}
        </div>
      );
    })}
  </>
);

const ActionMenu: React.FC<React.PropsWithChildren<ActionMenuProps>> = ({
  buttonText,
  variant = 'secondary',
  menuItems,
  iconName,
  color = variant === 'primary' ? 'bmtWhite' : 'bmtGrey90',
  portal = true,
  trackingEventData,
  ...menuButtonProps
}) => {
  const sambaIconColor = color === 'bmtWhite' ? 'icon.onPrimary' : 'icon';

  if (iconName) {
    return (
      <Box>
        <Menu>
          <MenuButton
            variant={variant}
            color={color}
            data-track-event="ElementClicked"
            data-track-module={trackingEventData?.module}
            data-track-name={trackingEventData?.name}
            {...menuButtonProps}
            minWidth="0"
          >
            <Flex aria-label="Action Menu Button">
              <Icon name={iconName} size="small" color={sambaIconColor} />
            </Flex>
          </MenuButton>
          <MenuList portal={portal}>
            <ActionMenuItems menuItems={menuItems} />
          </MenuList>
        </Menu>
      </Box>
    );
  }

  return (
    <Box>
      <Menu>
        <StyledMenuButton
          variant={variant}
          color={color}
          size="small"
          pt="4"
          pb="4"
          lineHeight="0"
          data-track-event="ElementClicked"
          data-track-module={trackingEventData?.module}
          data-track-name={trackingEventData?.name}
          {...menuButtonProps}
        >
          <Flex alignItems="center" justifyContent="center" py="0" px="4">
            {buttonText}
            <Flex ml={3}>
              <Icon name="CaretDown" size="small" color={sambaIconColor} />
            </Flex>
          </Flex>
        </StyledMenuButton>
        <StyledMenuPopover portal={portal}>
          <StyledMenuItems>
            <ActionMenuItems menuItems={menuItems} />
          </StyledMenuItems>
        </StyledMenuPopover>
      </Menu>
    </Box>
  );
};

export default ActionMenu;
